// Third Party
import * as React from 'react';
import cx from 'classnames';

// Utils
import { ellipsifyText } from '@utils/string-helpers';

// UI
import { Button, ButtonColor, ButtonVariant } from '@components/button';
import { Typography, TypographyVariant } from '@components/typography';

type Props = {
  text: string;
  maxLines?: number;
  maxLength?: number;
  className?: string;
  buttonClassName?: string;
  showMoreLabel?: string;
  showLessLabel?: string;
  typographyVariant?: TypographyVariant;
  typographyVariantM?: TypographyVariant;
  testId?: string;
  buttonColor?: ButtonColor;
  buttonVariant?: ButtonVariant;
};

const checkOverflow = (textContainer: HTMLDivElement | null) => {
  if (textContainer) {
    return textContainer.offsetHeight < textContainer.scrollHeight;
  }
  return false;
};

export const ExpandableText: React.FC<Props> = ({
  text,
  maxLines = 8,
  maxLength = 550,
  className = '',
  buttonClassName = '',
  showMoreLabel = 'show more',
  showLessLabel = 'show less',
  typographyVariant = 'body2',
  typographyVariantM = 'body1',
  testId = 'data',
  buttonColor = 'primary',
  buttonVariant = 'text',
}) => {
  const [showMoreButton, setExpandableTextButton] = React.useState(true);
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const overflowingText = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (checkOverflow(overflowingText.current)) {
      setIsOverflowing(true);
      return;
    }
    setIsOverflowing(false);
  }, [text]);

  const shouldShowButton = isOverflowing || text.length > maxLength;
  const displayText = showMoreButton ? ellipsifyText(text, maxLength) : text;

  return (
    <div>
      <div>
        <div
          className={cx({ [`line-clamp-[${maxLines}]`]: showMoreButton })}
          ref={overflowingText}
        >
          <Typography
            variant={typographyVariant}
            variantM={typographyVariantM}
            component="p"
            className={cx('whitespace-pre-wrap text-ellipsis', className)}
            data-id={testId}
          >
            {displayText}
          </Typography>
        </div>

        {shouldShowButton && (
          <div className="flex w-full justify-end">
            <Button
              className={cx('-mr-3', buttonClassName)}
              variant={buttonVariant}
              color={buttonColor}
              size="sm"
              onClick={() => setExpandableTextButton(!showMoreButton)}
              data-id={`${testId}-${showMoreButton ? 'show-more' : 'show-less'}`}
            >
              {showMoreButton ? showMoreLabel : showLessLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
